import type { ConvexQueryClient } from "@convex-dev/react-query";
import * as Sentry from "@sentry/react";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import Toast from "react-native-toast-message";
import { handleAuthError } from "./auth-error-handler";

function isNetworkError(error: unknown): boolean {
  return (
    error instanceof TypeError && error.message.includes("Failed to fetch")
  );
}

const FUNCTION_NAME_SYMBOL = Symbol.for("functionName");

function extractFunctionName(queryKey: readonly unknown[]): string {
  if (queryKey.length < 2) return "unknown";
  const fnRef = queryKey[1];
  if (typeof fnRef === "string") return fnRef;
  if (typeof fnRef === "object" && fnRef !== null) {
    const name = (fnRef as Record<symbol, unknown>)[FUNCTION_NAME_SYMBOL];
    if (typeof name === "string") return name;
  }
  return "unknown";
}

function extractFunctionFromError(error: unknown): string {
  if (!(error instanceof Error)) return "unknown";
  const match = error.message.match(
    /\[(?:CONVEX\s+\w+\(.*?\))?\s*]?\s*(?:in\s+)?([a-zA-Z_][\w./:]*)/,
  );
  if (match?.[1]) return match[1];
  const convexMatch = error.message.match(
    /Uncaught Error:\s.*?(\w+[\w./:]+\w+)/,
  );
  return convexMatch?.[1] ?? "unknown";
}

function generateErrorId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  return `${timestamp}-${random}`;
}

function showErrorToast(message: string, errorId: string) {
  Toast.show({
    type: "error",
    text1: message,
    text2: `Ref: ${errorId}`,
    text1Style: {
      fontFamily: "Geologica_400Regular",
      fontSize: 10,
      color: "#000000",
    },
    text2Style: {
      fontFamily: "Geologica_400Regular",
      fontSize: 8,
      color: "#666666",
    },
  });
}

export const getQueryClient = (convexQueryClient: ConvexQueryClient) =>
  new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        const errorId = generateErrorId();
        const functionName = extractFunctionName(query.queryKey);

        Sentry.captureException(error, {
          level: isNetworkError(error) ? "warning" : "error",
          tags: {
            errorId,
            source: "react-query.query",
            convexFunction: functionName,
            ...(isNetworkError(error) && { errorType: "network" }),
          },
        });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        const errorId = generateErrorId();
        const functionName = mutation.options.mutationKey
          ? extractFunctionName(mutation.options.mutationKey)
          : extractFunctionFromError(error);

        Sentry.captureException(error, {
          level: isNetworkError(error) ? "warning" : "error",
          tags: {
            errorId,
            source: "react-query.mutation",
            convexFunction: functionName,
            ...(isNetworkError(error) && { errorType: "network" }),
          },
        });

        const { message } = handleAuthError(error);
        showErrorToast(message, errorId);
      },
    }),
    defaultOptions: {
      queries: {
        queryKeyHashFn: convexQueryClient.hashFn(),
        queryFn: convexQueryClient.queryFn(),
        retry: (failureCount, error) => {
          if (isNetworkError(error)) {
            return failureCount < 3;
          }
          return false;
        },
      },
    },
  });
